import classNames from "classnames";
import React from "react";

const classNamePrefix = "ContactCard";

type ContactCardRootProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> & { title: React.ReactNode; titleAs?: "h1" | "h2" | "h3" | "h4" | "h5" };

const ContactCardRoot = React.forwardRef(
  (props: ContactCardRootProps, ref?: React.ForwardedRef<HTMLDivElement>) => {
    const {
      children,
      className,
      title,
      titleAs: Title = "h3",
      ...restProps
    } = props;
    return (
      <div
        ref={ref}
        {...restProps}
        className={classNames(className, classNamePrefix)}
      >
        <Title className={`${classNamePrefix}-title`}>{title}</Title>
        {children}
      </div>
    );
  },
);

type ContactCardItemProps = React.HTMLAttributes<HTMLDListElement> & {
  address?: React.ReactNode;
  email?: React.ReactNode;
  label: React.ReactNode;
  name?: React.ReactNode;
  phoneNumber?: React.ReactNode;
};

const ContactCardItem = React.forwardRef(
  (props: ContactCardItemProps, ref?: React.ForwardedRef<HTMLDListElement>) => {
    const {
      address,
      children,
      className,
      email,
      label,
      name,
      phoneNumber,
      ...restProps
    } = props;
    return (
      <dl
        ref={ref}
        {...restProps}
        className={classNames(className, `${classNamePrefix}-item`)}
      >
        <dt className={`${classNamePrefix}-item-label`}>{label}</dt>
        {name && (
          <dd className={`${classNamePrefix}-item-value`} data-role="name">
            {name}
          </dd>
        )}
        {address && (
          <dd className={`${classNamePrefix}-item-value`} data-role="address">
            {address}
          </dd>
        )}
        {phoneNumber && (
          <dd
            className={`${classNamePrefix}-item-value`}
            data-role="phoneNumber"
          >
            <a
              aria-label="Valider pour nous contacter par téléphone"
              href={`tel:${phoneNumber}`}
            >
              {phoneNumber}
            </a>
          </dd>
        )}
        {email && (
          <dd className={`${classNamePrefix}-item-value`} data-role="email">
            <a
              aria-label="Valider pour nous contacter par email"
              href={`mailto:${email}`}
            >
              {email}
            </a>
          </dd>
        )}
        {children}
      </dl>
    );
  },
);

export const ContactCard = Object.assign(ContactCardRoot, {
  Item: ContactCardItem,
});
